import { cn } from "@/lib/cn";
import {
  getBlurhashFromUrl,
  getMobileMediaCapVars,
  useMediaLazyLoad,
  validateDynamicVariable,
} from "@/shared";
import Blurhash from "@components/blurhash";
import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import { LinkPopUp } from "@library/components/LinkPopUp";
import { useCallback, useMemo, useState, type MouseEvent } from "react";
import { useMediaAnimation } from "../hooks";
import {
  ImageContainerStyled,
  ImageElementStyled,
} from "../styles/Image.styled";
import "../styles/index.scss";
import type { EditorDataProps, EditorSlotsProps, ImageProps } from "../types";

const LINK_ATTRIBUTES = ({
  settings,
  isLink,
}: {
  settings: ImageProps;
  isLink: boolean;
}) => {
  if (!isLink || !settings.linkUrl) return {};
  return {
    href: settings.linkUrl,
    target: "_blank",
    "aria-label": settings.ariaLabel || "",
    as: "a",
  };
};

const STYLED_ATTRIBUTES = ({ settings }: { settings: ImageProps }) => ({
  $round: settings.round,
  $isLogo: settings.name === "Logo",
  ...(settings.metadata && {
    $metadata: {
      width: settings.metadata.width,
      height: settings.metadata.height,
    },
  }),
  $shape: settings.shape ?? "free",
  $shadow: settings.shadow,
  $shadowColor: settings.shadowColor,
  $shadowOpacity: settings.shadowOpacity,
  $overlayOpacity: settings.overlayOpacity,
  $overlayColor: settings.overlayColor,
  $blobType: settings.blobType,
  $parallelogramDirection: settings.parallelogramDirection,
});

const DATA_ATTRIBUTES = ({
  settings,
  isPdf,
}: {
  settings: ImageProps;
  isPdf: boolean;
}) => {
  const options: Record<string, string> = {};
  if (!settings.animation || isPdf) return options;
  options["data-animation"] = settings.animation.toString();
  const { animationType, animationStyle, animationDirection } = settings;
  if (!animationStyle || animationStyle === "zoom") {
    options["data-animation-style"] = animationStyle || "zoom";
    options["data-animation-type"] = animationType || "zoomIn";
  } else {
    options["data-animation-style"] = animationStyle || "slide";
    options["data-animation-direction"] = animationDirection || "left";
  }
  return options;
};

export const ImageLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const { view } = useViewContext();
  const { settings } = entity as StaticEntityType<ImageProps>;
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [showLinkPopUp, setShowLinkPopUp] = useState(false);

  const isPdf = view === "pdf";
  const isEditor = view === "editor";
  const isLogo = settings.name === "Logo";

  const DND_ATTR = isEditor ? { "data-dnd-trigger": "true" } : {};
  const { ref } = useMediaAnimation({ settings });
  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;
  const { setObserverRef, isVisible } = useMediaLazyLoad();
  const isFreeShape = settings.shape === "free" || !settings.shape;

  // ===== IMAGE SRC =====
  // As static-library's copy: a bound image variable's value, once filled in
  // (the create-version builder's preview compiles it into
  // dynamicVariableSrc), wins over the design-time src. A raw `{{token}}`
  // (the editor canvas resolves its own) leaves src alone.
  const imgSrc = useMemo(() => {
    let src = settings.src || "/assets/images/image-placeholder.jpg";

    const { isDynamic, dynamicVariableParsed } = validateDynamicVariable(
      settings.dynamicVariableSrc,
    );
    if (dynamicVariableParsed && isDynamic && settings.dynamicVariableSrc) {
      const parsedSrc = settings.dynamicVariableSrc.replace(/&quot;/g, '"');
      // Normally a plain URL; some sources send { url, domain, theme }.
      let temp = parsedSrc;
      if (parsedSrc.trimStart().startsWith("{")) {
        try {
          const parsed = JSON.parse(parsedSrc);
          if (parsed && typeof parsed.url === "string") temp = parsed.url;
        } catch {
          /* not a JSON payload - fall back to the raw value */
        }
      }
      if (temp) src = temp;
    }

    if (settings.customDynamicVariable?.startsWith("https://")) {
      src = settings.customDynamicVariable;
    }

    if (src.includes("data:")) {
      src = src.split("?")[0];
    }
    return src;
  }, [
    settings.src,
    settings.dynamicVariableSrc,
    settings.customDynamicVariable,
  ]);

  const hash = getBlurhashFromUrl(imgSrc);

  // ===== LINK =====
  // `isLink` means "render the container as an <a href>" - only a hyperlink
  // can do that.
  const isLink = useMemo(
    () =>
      (settings.isLinkActive && !settings.selectedLinkType) ||
      settings.selectedLinkType === "hyperlink",
    [settings.isLinkActive, settings.selectedLinkType],
  );

  // `isClickable` is the affordance signal - it covers every link type that
  // actually navigates. An anchor link scrolls through the element's
  // `data-anchor-trigger` handler and carries no href, which is why it must
  // not be folded into `isLink` above. The untyped branch mirrors the Button
  // element: stories authored before `selectedLinkType` existed carry only an
  // `anchorTrigger`, and useAnchorTrigger still navigates them - it bails only
  // on a type that is set and is not "anchor". Requiring a real target ties the
  // zoom to actual navigability; ElementStyled's `[data-anchor-trigger]` cursor
  // rule is looser (it keys off the attribute alone), so an anchor type with no
  // target picked yet still shows a pointer but correctly gets no zoom.
  const isClickable = useMemo(
    () =>
      isLink ||
      (!!settings.anchorTrigger &&
        (!settings.selectedLinkType || settings.selectedLinkType === "anchor")),
    [isLink, settings.selectedLinkType, settings.anchorTrigger],
  );

  // ===== CLICK HANDLER =====
  const handleOnClick = useCallback(
    (e: MouseEvent) => {
      if (
        settings.selectedLinkType === "hyperlink" &&
        !editorData &&
        settings.openInPopup &&
        settings.linkUrl
      ) {
        e.preventDefault();
        setShowLinkPopUp(true);
      }
    },
    [
      settings.selectedLinkType,
      settings.openInPopup,
      settings.linkUrl,
      editorData,
    ],
  );

  // ===== SCALE AND POSITION =====
  const { scale, position } = useMemo(() => {
    return {
      scale: settings.metadata?.scale ?? 1,
      position: {
        x: settings.metadata?.translateX ?? 50,
        y: settings.metadata?.translateY ?? 50,
      },
    };
  }, [
    settings.metadata?.scale,
    settings.metadata?.translateX,
    settings.metadata?.translateY,
  ]);

  const imageInlineStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    if (view === "editor") {
      style.willChange = "transform, object-position, transform-origin";
    }
    if (position.x !== 50 || position.y !== 50) {
      style.transformOrigin = `${position.x}% ${position.y}%`;
      style.objectPosition = `${position.x}% ${position.y}%`;
    }
    return style;
  }, [scale, position, view]);

  // ===== ASPECT RATIO =====
  const metaAspectRatio = useMemo(() => {
    if (
      settings.metadata &&
      settings.metadata.width > 0 &&
      isFreeShape &&
      imgSrc.includes("?b=")
    ) {
      return settings.metadata.height / settings.metadata.width;
    }
    return undefined;
  }, [settings.metadata, isFreeShape, imgSrc]);

  // ===== CONTAINER STYLES =====
  const containerStyle = useMemo(
    () =>
      ({
        alignItems: settings.verticalAlignment || "inherit",
        justifyContent: settings.horizontalAlignment || "inherit",
        "--entity-intiial-scale": scale,
      }) as React.CSSProperties,
    [settings.verticalAlignment, settings.horizontalAlignment, scale],
  );

  // ===== IMAGE CONTAINER STYLES =====
  const imageContainerStyle = useMemo(() => {
    const widthNum = width != null ? Number(width) : 100;
    const isNarrowCombo = entity.layers?.combo?.settings?.maxWidth === "narrow";
    return {
      "--entity-width": `${widthNum}%`,
      "--entity-height": height ? `${height}px` : "auto",
      "--entity-padding": height ? "unset" : "56.25%",
      ...getMobileMediaCapVars(width, settings.shape, isNarrowCombo),
    } as React.CSSProperties;
  }, [width, height, settings.shape, entity.layers?.combo?.settings?.maxWidth]);

  // ===== BLURHASH STYLE =====
  const blurhashStyle = useMemo(
    () => ({
      borderRadius: settings.round,
    }),
    [settings.round],
  );

  // ===== REF CALLBACK =====
  const setRefs = useCallback(
    (r: HTMLDivElement | null) => {
      if (!r) return;
      const elementRef = editorData?.elementRef;
      if (elementRef) {
        elementRef.current = r;
      }
      if (ref) ref(r);
      setObserverRef(r);
    },
    [editorData, ref, setObserverRef],
  );

  // ===== CONTAINER REF CALLBACK =====
  const setContainerRef = useCallback(
    (r: HTMLDivElement | null) => {
      if (!r) return;
      const imageContainerRef = editorData?.imageContainerRef;
      if (imageContainerRef) {
        imageContainerRef.current = r;
      }
    },
    [editorData?.imageContainerRef],
  );

  // ===== MEDIA LOAD HANDLERS =====
  const handleMediaLoad = useCallback(() => setIsMediaLoaded(true), []);
  const handleMediaLoadStart = useCallback(() => setIsMediaLoaded(false), []);

  // ===== CLOSE POPUP HANDLER =====
  const closePopUp = useCallback(() => setShowLinkPopUp(false), []);

  return (
    <div
      className={cn("imageWrapper tw-relative tw-flex", {
        "tw-border tw-border-dashed tw-border-[#4CBA78]":
          !!editorData?.isAdmin && !!settings.AIClassificationAnchor,
      })}
      style={containerStyle}
      ref={setRefs}
      data-element-shape={settings.shape || "free"}
      data-analytics-click-event={settings.linkAnalyticEvent}
    >
      {editorSlots?.resizeSlot}
      <ImageContainerStyled
        {...DND_ATTR}
        className={cn("tw-max-w-full tw-relative tw-z-[6] tw-flex", {
          imageLayout: !isLogo,
          logoLayout: isLogo,
          "--link": isClickable,
          "max-md:!tw-w-1/2": isLogo,
        })}
        ref={setContainerRef}
        data-src={imgSrc}
        onDoubleClick={editorData?.handleMediaDialog}
        onClick={handleOnClick}
        style={imageContainerStyle}
        {...STYLED_ATTRIBUTES({ settings })}
        {...LINK_ATTRIBUTES({ settings, isLink })}
        {...DATA_ATTRIBUTES({ settings, isPdf })}
      >
        <div
          className={cn(
            "imageWrapperInner tw-relative tw-w-full tw-overflow-hidden",
            {
              "after:tw-absolute after:tw-inset-0 after:tw-size-full after:tw-[background:var(--overlay-color)] after:tw-content-['']":
                !!settings.overlay,
            },
          )}
          style={{
            paddingTop: metaAspectRatio
              ? `${metaAspectRatio * 100}%`
              : undefined,
          }}
        >
          {hash && !isMediaLoaded && (
            <Blurhash blurhash={hash} style={blurhashStyle} />
          )}
          {isVisible && (
            <ImageElementStyled
              width={settings.metadata?.width}
              height={settings.metadata?.height}
              src={imgSrc}
              alt={settings.alt || ""}
              $round={settings.round}
              $filter={settings.blackAndWhite}
              $blobType={settings.blobType}
              $parallelogramDirection={settings.parallelogramDirection}
              $shape={settings.shape}
              data-shape={settings.shape}
              style={imageInlineStyle}
              className={cn(
                "tw-absolute tw-inset-0 tw-block tw-h-full tw-w-full tw-object-cover",
                {
                  "tw-object-contain": isFreeShape,
                  "tw-static": isFreeShape && !metaAspectRatio,
                  "tw-transition-transform tw-duration-200": isClickable,
                },
              )}
              draggable="false"
              onLoad={handleMediaLoad}
              onLoadStart={handleMediaLoadStart}
            />
          )}
        </div>

        {editorSlots?.dynamicVariablePlateSlot}
        {editorSlots?.dynamicVariableDotSlot}
      </ImageContainerStyled>
      {showLinkPopUp && (
        <LinkPopUp src={settings.linkUrl} closePopUp={closePopUp} />
      )}
    </div>
  );
};
